import React from 'react';

import { config, moment } from 'data';
import { formatter } from 'helpers';
import { payoutService } from 'services';
import { useLang, useQuery, useTable, useTableQuery } from 'hooks';
import { TableView, WithdrawalStatus } from 'components/layout';
import { DateRangePicker, Form, Select } from 'components/ui';
import { TableColumns } from 'types/components';
import { Payout, WithdrawalStatus as EWithdrawalStatus } from 'types/models';
import { PayoutsParams } from 'types/services';
import { DatesPartialParam } from 'types/views';

import styles from './styles.module.css';

type TableParams = PayoutsParams & DatesPartialParam;

const initialTableParams: TableParams = {
  //
};

const PayoutsList = () => {
  const lang = useLang();
  const table = useTable<Payout, TableParams>([config.PAYOUTS_QUERY_KEY], initialTableParams);

  const payoutsParams: PayoutsParams = {
    page: table.page,
    status: table.params.status || undefined,
    dateFrom: table.params.dates?.[0] || undefined,
    dateTo: table.params.dates?.[1] || undefined,
  };

  const payoutsQuery = useQuery({
    queryKey: [config.PAYOUTS_QUERY_KEY, payoutsParams],
    queryFn: () => payoutService.getPayouts(payoutsParams),
  });

  useTableQuery(table, payoutsQuery);

  const columns: TableColumns<Payout> = [
    {
      key: 'id',
      title: lang.get('common.fields.id'),
      render: (_, payout) => formatter.formatIdTruncated(payout.id),
    }, {
      className: styles.table__date,
      key: 'date',
      title: lang.get('common.fields.date'),
      render: (_, payout) => formatter.formatDateTime(payout.createdDate),
    }, {
      className: styles.table__amount,
      key: 'amount',
      title: `${lang.get('common.fields.amount')}, ${config.DEFAULT_CURRENCY}`,
      render: (_, payout) => formatter.formatNumber(formatter.transformFromCents(payout.amount, payout.currency.decimals)),
    }, {
      className: styles.table__fee,
      key: 'fee',
      title: `${lang.get('common.fields.fee')}, ${config.DEFAULT_CURRENCY}`,
      render: (_, payout) => formatter.formatNumber(formatter.transformFromCents(payout.feeAmount, payout.currency.decimals)),
    }, {
      key: 'status',
      title: lang.get('common.fields.status'),
      render: (_, payout) => <WithdrawalStatus status={payout.status} />,
    },
  ];

  return (
    <TableView>

      <TableView.Filters<TableParams>
        initialValues={initialTableParams}
        values={table.params}
        onSubmit={table.setParams}
      >
        <Form.Item name="status" label={lang.get('common.filters.status')}>
          <Select
            placeholder={lang.get('common.actions.all')}
            options={Object.values(EWithdrawalStatus).map((status) => ({
              value: status,
              label: lang.get(`withdrawal.statuses.${status.toLowerCase()}`),
            }))}
            allowClear
            sort
          />
        </Form.Item>
        <Form.Item name="dates" label={lang.get('common.filters.date')}>
          <DateRangePicker maxDate={moment()} allowEmpty />
        </Form.Item>
      </TableView.Filters>

      <TableView.Table<Payout>
        table={table}
        columns={columns}
        rowKey={(payout) => payout.id}
        loading={payoutsQuery.isLoading}
      />

    </TableView>
  );
};

export default PayoutsList;
